/**
 * Typed wrappers for the `/salescoach/api/user/:username/*` endpoints (Plan 04-09
 * Task 1). Eight fetchers — one per backend route — mirroring the structure
 * of `quality-api.ts` / `operations-api.ts`:
 *
 *   1. `buildQuery(filters)` strips defaults + resolves preset → from/to.
 *   2. Granularity / limit / offset are appended as extra params.
 *   3. Return type is always the raw `ApiEnvelope<T>` — unwrapping is the
 *      hook's job (`use-user-drilldown.ts`).
 *
 * Path segments use `encodeURIComponent(username)` so special characters in
 * the username don't break URL parsing. The param is `:username` end-to-end
 * per F-02 + Plan 04-04 (WARNING-12 — no `:id` remnants).
 *
 * 404 handling: `fetchUserProfile` throws a typed `UserNotFoundError` on HTTP
 * 404 so the hook layer can distinguish "unknown user" from generic errors
 * and render a dedicated 404 screen.
 */

import { apiFetch, ApiError } from '@/services/api';
import { buildApiUrl } from '@/services/api-client';
import type { Filters } from '@/lib/filters-schema';
import type {
  ApiEnvelope,
  UserChartsData,
  UserChartsSource,
  UserKPIs,
  UserProfile,
  UserSafetyRefusalRow,
  UserSkillRow,
  UserToolRow,
} from './api-types';

// ---------------------------------------------------------------------------
// UserNotFoundError — typed 404 so hooks can distinguish unknown-user from
// network / auth / server errors. The backend `GET /profile` returns 404
// with `{ error: 'user_not_found', userId }` when the user has no traces.
// ---------------------------------------------------------------------------

export class UserNotFoundError extends Error {
  readonly status = 404;

  readonly username: string;

  constructor(username: string) {
    super(`User not found: ${username}`);
    this.name = 'UserNotFoundError';
    this.username = username;
  }
}

// ---------------------------------------------------------------------------
// Endpoint wrappers
// ---------------------------------------------------------------------------

/**
 * GET /salescoach/api/user/:username/profile — lifetime-scoped profile (no filter
 * params on the wire). Throws `UserNotFoundError` on HTTP 404 so the hook
 * can branch into the "User not found" screen.
 */
export async function fetchUserProfile(
  username: string,
): Promise<ApiEnvelope<UserProfile>> {
  try {
    return await apiFetch<ApiEnvelope<UserProfile>>(
      `/salescoach/api/user/${encodeURIComponent(username)}/profile`,
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      throw new UserNotFoundError(username);
    }
    throw err;
  }
}

export async function fetchUserKpis(
  username: string,
  filters: Filters,
): Promise<ApiEnvelope<UserKPIs>> {
  return apiFetch<ApiEnvelope<UserKPIs>>(
    buildApiUrl(`/salescoach/api/user/${encodeURIComponent(username)}/kpis`, filters),
  );
}

export async function fetchUserCharts(
  username: string,
  filters: Filters,
  granularity: Filters['granularity'],
  source: UserChartsSource = 'all',
): Promise<ApiEnvelope<UserChartsData>> {
  // Only send `source` when it's not the default — keeps cache keys tight
  // and mirrors how other api-client fetchers omit default params. `buildApiUrl`
  // treats `undefined` values as "skip this param".
  return apiFetch<ApiEnvelope<UserChartsData>>(
    buildApiUrl(
      `/salescoach/api/user/${encodeURIComponent(username)}/charts`,
      filters,
      { granularity, source: source === 'all' ? undefined : source },
    ),
  );
}

/** Shared top-tools/top-skills limit query param — server default 5, max 20. */
export interface TopListParams {
  limit?: number;
}

export async function fetchUserTopTools(
  username: string,
  filters: Filters,
  params: TopListParams = {},
): Promise<ApiEnvelope<UserToolRow[]>> {
  return apiFetch<ApiEnvelope<UserToolRow[]>>(
    buildApiUrl(
      `/salescoach/api/user/${encodeURIComponent(username)}/top-tools`,
      filters,
      { limit: params.limit },
    ),
  );
}

export async function fetchUserTopSkills(
  username: string,
  filters: Filters,
  params: TopListParams = {},
): Promise<ApiEnvelope<UserSkillRow[]>> {
  return apiFetch<ApiEnvelope<UserSkillRow[]>>(
    buildApiUrl(
      `/salescoach/api/user/${encodeURIComponent(username)}/top-skills`,
      filters,
      { limit: params.limit },
    ),
  );
}

export async function fetchUserSafetyRefusals(
  username: string,
  filters: Filters,
): Promise<ApiEnvelope<UserSafetyRefusalRow[]>> {
  return apiFetch<ApiEnvelope<UserSafetyRefusalRow[]>>(
    buildApiUrl(
      `/salescoach/api/user/${encodeURIComponent(username)}/safety-refusals`,
      filters,
    ),
  );
}
